// Section "Orchestration du serveur" : vue globale des traitements de tous les environnements d'un
// serveur (une bande par environnement), bandeau de simultaneite, puis tableau. L'appel n'a lieu
// qu'avec un serveur selectionne ; le filtre d'heures est purement local (aucun appel en plus).
import * as d3 from "d3";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin, Table, Tag } from "antd";

import axios from "../../utils/axios";
import useResize from "../../hooks/useResize";
import { proprietesPagination } from "./cartographieDonnees";
import {
  BarreFiltres,
  libelleDepuis,
  libelleJusqua,
  usePeriode,
  GOUTTIERE,
  LIBELLES_STATUT,
  StatutRun,
  libelleStale,
  libelleStaleNote,
  nbStale,
  definirHachures,
  dessinerAxe,
  dessinerBarres,
  dessinerGrille,
  useOuvrirLogs,
} from "./FriseCommune";
import {
  PLAGE_DEFAUT,
  construireEchelle,
  couleurEnv,
  finEffective,
  debutAxe,
  intersecteTranches,
  libelleInstant,
  ms,
  ordresConcurrents,
  paliersSimultanes,
  picSimultane,
  plageComplete,
  rangerEnLignes,
  tranchesHoraires,
} from "./frise";
import { clePeriode, nbJoursFenetre, paramsApi, premierJour, titrePlage } from "./periode";
import { formaterDebutFin, formaterDureeS, trierRuns } from "./Orchestration";

const MAX_LIGNES = 6; // sous-lignes par bande ; au-dela, ligne de debordement "+N"
const LIGNE_S = 16; // hauteur d'une sous-ligne de bande
const BANDE_PAD = 6;
const MAX_LIGNES_STALE = 3; // sous-lignes de tete reservees aux runs bloques (hors limite de 6)
const SIM_H = 46; // bandeau de simultaneite
const ID_HACHURES = "orchsrv-hachures";
const liste = (v) => (Array.isArray(v) ? v : []);
const tronquerTexte = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

export default function OrchestrationServeur({ serverId, libelle, plage: plageProp, onPlage }) {
  const [periode, { setDays, setRange }] = usePeriode();
  const clePer = clePeriode(periode);
  const [oldest, setOldest] = useState(null);
  const [plageLocale, setPlageLocale] = useState(PLAGE_DEFAUT);
  const plage = plageProp || plageLocale;
  const majPlage = onPlage || setPlageLocale;
  const [etat, setEtat] = useState({ chargement: false, erreur: false, data: null });
  const [survol, setSurvol] = useState(null);
  const [page, setPage] = useState({ current: 1, pageSize: 50 });
  const conteneur = useRef();
  const svgRef = useRef();
  const axeRef = useRef();
  const size = useResize(conteneur);
  const ouvrirLogs = useOuvrirLogs();
  const ouvrirRef = useRef(ouvrirLogs);
  ouvrirRef.current = ouvrirLogs;
  const largeur = (size && size.width) || 900;

  useEffect(() => {
    if (serverId === null || serverId === undefined) return undefined;
    let actif = true; // serveur ou periode change : la reponse perimee est ignoree
    setEtat({ chargement: true, erreur: false, data: null });
    setSurvol(null);
    axios
      .get(`/api/supervision/servers/${serverId}/runs`, { params: paramsApi(periode) })
      .then((res) => {
        if (!actif) return;
        setOldest(premierJour(res && res.data && res.data.oldest_available) || null);
        setEtat({ chargement: false, erreur: false, data: (res && res.data) || {} });
      })
      .catch(() => actif && setEtat({ chargement: false, erreur: true, data: null }));
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, clePer]);

  useEffect(() => {
    setPage((p) => ({ ...p, current: 1 }));
  }, [etat.data, plage]);

  const data = etat.data;
  const fin = ms(data && data.to);
  const tous = useMemo(() => trierRuns(data && data.runs), [data]);
  const tranches = useMemo(() => (data ? tranchesHoraires(debutAxe(data), data.to, plage) : []), [data, plage]);
  const runs = useMemo(
    () => tous.filter((r) => intersecteTranches(tranches, ms(r.start), finEffective(r, fin))),
    [tous, tranches, fin]
  );

  // Environnements (tries par code) : couleur stable par rang, meme quand le filtre masque des runs.
  const environnements = useMemo(() => {
    const parId = new Map();
    liste(data && data.environments).forEach((e) => parId.set(e.id, { id: e.id, code: e.code, label: e.label }));
    tous.forEach((r) => {
      if (!parId.has(r.environment_id)) parId.set(r.environment_id, { id: r.environment_id, code: r.environment_code, label: "" });
    });
    return [...parId.values()].sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [data, tous]);
  const couleurs = useMemo(() => new Map(environnements.map((e, i) => [e.id, couleurEnv(i)])), [environnements]);

  // Une bande par environnement ayant des runs conserves ; sous-lignes sans superposition.
  const bandes = useMemo(() => {
    const parEnv = new Map();
    runs.forEach((r) => {
      if (!parEnv.has(r.environment_id)) parEnv.set(r.environment_id, []);
      parEnv.get(r.environment_id).push(r);
    });
    let y = 0;
    return environnements
      .filter((e) => parEnv.has(e.id))
      .map((e) => {
        const rs = parEnv.get(e.id);
        // runs bloques (stale) : sous-lignes de tete a eux, hors limite des 6 sous-lignes / « +N »
        const bloques = rs.filter((r) => r.stale);
        const normaux = rs.filter((r) => !r.stale);
        const st = rangerEnLignes(bloques, fin, MAX_LIGNES_STALE - 1);
        const nbSt = st.nb;
        const nm = rangerEnLignes(normaux, fin, MAX_LIGNES);
        const lignes = new Map();
        st.lignes.forEach((i, o) => lignes.set(o, Math.min(i, MAX_LIGNES_STALE - 1)));
        nm.lignes.forEach((i, o) => lignes.set(o, i + nbSt));
        const debordes = nm.debordes;
        const h = (nbSt + nm.nb) * LIGNE_S + BANDE_PAD * 2;
        const b = { env: e, runs: rs, lignes, debordes, nbSt, h, y };
        y += h;
        return b;
      });
  }, [runs, environnements, fin]);
  const hauteurBandes = bandes.reduce((s, b) => s + b.h, 0);
  const sansTraitement = environnements.filter((e) => !tous.some((r) => r.environment_id === e.id)).length;

  const paliers = useMemo(() => paliersSimultanes(runs, fin), [runs, fin]);
  const pic = useMemo(() => {
    if (plageComplete(plage) && data && data.peak && data.peak.count) {
      return { count: data.peak.count, at: ms(data.peak.at) };
    }
    return picSimultane(paliers, tranches);
  }, [plage, data, paliers, tranches]);
  const lies = useMemo(() => ordresConcurrents(runs, survol, fin), [runs, survol, fin]);

  useEffect(() => {
    if (!svgRef.current || !axeRef.current || !data || runs.length === 0) return;
    const w = Math.max(300, largeur - GOUTTIERE - 16);
    const ech = construireEchelle(tranches, GOUTTIERE, w);
    const nbJours = nbJoursFenetre(periode, data);
    const hauteur = SIM_H + hauteurBandes + 4;
    dessinerAxe(d3.select(axeRef.current), ech, nbJours, largeur, plage.origine || 0);
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", largeur).attr("height", hauteur);
    definirHachures(svg, ID_HACHURES);
    dessinerGrille(svg, ech, nbJours, hauteur, plage.origine || 0);

    // Bandeau de simultaneite : escalier aligne sur l'axe (par tranche), echelle = pic.
    const gs = svg.append("g").attr("class", "orch-simultane");
    gs.append("text").attr("x", 4).attr("y", 14).attr("font-size", 11).attr("font-weight", 600).attr("fill", "#1d2733").text("Simultanéité");
    gs.append("text").attr("x", 4).attr("y", 28).attr("font-size", 10).attr("fill", "#6b7785").text(pic.count ? `max ${pic.count}` : "");
    const maxN = Math.max(1, pic.count, ...paliers.map((p) => p.n));
    const yN = (n) => SIM_H - 4 - (n / maxN) * (SIM_H - 10);
    let trait = "";
    let finPrec = null;
    paliers.forEach((p) => {
      ech.segments(p.t0, p.t1).forEach((s) => {
        if (p.n > 0) {
          gs.append("rect").attr("class", "palier").attr("x", s.x0).attr("y", yN(p.n)).attr("width", Math.max(0, s.x1 - s.x0)).attr("height", SIM_H - 4 - yN(p.n)).attr("fill", "rgba(25,92,132,.28)");
        }
        trait += `${finPrec !== null && Math.abs(finPrec - s.x0) < 0.5 ? "L" : "M"}${s.x0},${yN(p.n)}L${s.x1},${yN(p.n)}`;
        finPrec = s.x1;
      });
    });
    gs.append("path").attr("class", "courbe-simultane").attr("d", trait).attr("fill", "none").attr("stroke", "#195c84").attr("stroke-width", 1.5);
    gs.append("line").attr("x1", 0).attr("x2", largeur).attr("y1", SIM_H).attr("y2", SIM_H).attr("stroke", "#c3cbd5");

    // Bandes : fond alterne, libelle "code · raison sociale", runs en sous-lignes.
    bandes.forEach((b, i) => {
      const gb = svg.append("g").attr("class", "bande").attr("data-env-id", b.env.id).attr("transform", `translate(0,${SIM_H + b.y})`);
      gb.append("rect").attr("class", "bande-fond").attr("x", 0).attr("y", 0).attr("width", GOUTTIERE - 4).attr("height", b.h).attr("fill", i % 2 ? "#f6f8fa" : "#fff").attr("opacity", 0.9);
      gb.append("line").attr("x1", 0).attr("x2", largeur).attr("y1", b.h - 0.5).attr("y2", b.h - 0.5).attr("stroke", "#e3e8ee");
      if (b.debordes > 0) {
        const lib = gb.append("text").attr("class", "bande-plus").attr("x", 4).attr("y", BANDE_PAD + (b.nbSt + MAX_LIGNES) * LIGNE_S + 12).attr("font-size", 10).attr("font-weight", 600).attr("fill", "#6b7785").text(`+${b.debordes}`);
        lib.append("title").text(`${b.debordes} ${b.debordes > 1 ? "jobs supplémentaires" : "job supplémentaire"}`);
      }
      b.runs.forEach((r) => {
        const deborde = !r.stale && (b.lignes.get(r.order) || 0) >= MAX_LIGNES + b.nbSt;
        const g = gb
          .append("g")
          .datum(r)
          .attr("class", "run")
          .attr("data-order", r.order)
          .attr("data-deborde", deborde ? "1" : null)
          .style("cursor", "pointer")
          .on("mouseenter", () => setSurvol(r.order))
          .on("mouseleave", () => setSurvol(null))
          .on("click", () => ouvrirRef.current(r));
        dessinerBarres(g, ech.segments(ms(r.start), finEffective(r, fin)), {
          couleur: couleurs.get(r.environment_id),
          statut: r.status,
          jusqua: libelleJusqua(r.end ? ms(r.end) : NaN, fin),
          depuis: libelleDepuis(ms(r.start), +new Date(debutAxe(data))),
          stale: r.stale,
          libelleStale: r.stale ? libelleStale(r) : null,
          opacite: deborde ? 0.35 : null,
          y: BANDE_PAD + (b.lignes.get(r.order) || 0) * LIGNE_S + 1,
          h: LIGNE_S - 2,
          idHachures: ID_HACHURES,
          titre: [
            `${r.environment_code}`,
            `${r.label || r.filename} (${r.filename})`,
            `${formaterDebutFin(r.start)} → ${r.end ? formaterDebutFin(r.end) : r.status === "running" ? "en cours" : "—"} (${formaterDureeS(r.duration_s)})`,
            `Statut : ${LIBELLES_STATUT[r.status] || LIBELLES_STATUT.unknown}${r.stale ? " — bloqué ? (en cours depuis plus de 24 h)" : ""}`,
          ].join("\n"),
        });
      });
      gb.append("text").attr("class", "bande-libelle").attr("x", 4).attr("y", BANDE_PAD + 11).attr("font-size", 11).attr("font-weight", 600).attr("fill", couleurs.get(b.env.id)).attr("stroke", "#fff").attr("stroke-width", 3).attr("paint-order", "stroke").text(tronquerTexte(`${b.env.code}${b.env.label ? ` · ${b.env.label}` : ""}`, 32));
    });
  }, [runs, tranches, data, largeur, clePer, plage, bandes, couleurs, paliers, pic, hauteurBandes, fin]);

  // Survol : le run et les runs concurrents (tous environnements) restent opaques, le reste est estompe.
  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current)
      .selectAll("g.run")
      .attr("class", (r) => `run${survol === null ? "" : lies.has(r.order) ? " lie" : " estompe"}`)
      .attr("opacity", (r) => (survol === null || lies.has(r.order) ? 1 : 0.25));
  }, [survol, lies, runs, bandes]);

  const colonnes = [
    { title: "N°", dataIndex: "order", key: "order", width: 60 },
    {
      title: "Environnement",
      key: "env",
      render: (_, r) => (
        <span>
          <span className="carto-dot" style={{ background: couleurs.get(r.environment_id) }} /> {r.environment_code}
        </span>
      ),
    },
    {
      title: "Job",
      key: "job",
      render: (_, r) => (
        <span>
          {r.label || r.filename}
          <div className="carto-sub">{r.filename}</div>
        </span>
      ),
    },
    { title: "Prestataire", dataIndex: "provider", key: "provider", render: (p) => p || "—" },
    { title: "Début", dataIndex: "start", key: "start", render: formaterDebutFin },
    { title: "Fin", dataIndex: "end", key: "end", render: (v, r) => (v ? formaterDebutFin(v) : r.status === "running" ? "en cours" : "—") },
    { title: "Durée", dataIndex: "duration_s", key: "duree", render: formaterDureeS },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (s, r) => <StatutRun run={r} />,
    },
    { title: "Simultanés", dataIndex: "concurrent", key: "concurrent", render: (c) => (c === null || c === undefined ? "—" : c) },
  ];

  const nbJours = nbJoursFenetre(periode, data);
  const nom = (data && data.server && data.server.label) || libelle || "";
  const max = (data && data.max_runs) || 5000;
  return (
    <div className="carto-orchestration" data-testid="orchestration-serveur" ref={conteneur}>
      <div className="carto-orch-tete">
        <h3>
          {periode.mode === "range"
            ? `Orchestration du serveur ${nom} — ${titrePlage(periode.from, periode.to)}`
            : `Orchestration du serveur ${nom} — ${nbJours} ${nbJours > 1 ? "derniers jours" : "dernier jour"}`}
        </h3>
        <BarreFiltres periode={periode} onDays={setDays} onRange={setRange} oldest={oldest} plage={plage} onPlage={majPlage} />
      </div>
      {etat.chargement && (
        <div className="carto-vide">
          <Spin />
        </div>
      )}
      {etat.erreur && <Alert type="error" showIcon message="Impossible de charger l'orchestration de ce serveur" />}
      {!etat.chargement && !etat.erreur && data && tous.length === 0 && (
        <div className="carto-vide">aucun job exécuté sur la période</div>
      )}
      {!etat.chargement && !etat.erreur && data && tous.length > 0 && runs.length === 0 && (
        <div className="carto-vide">aucun job sur cette plage horaire</div>
      )}
      {data && tous.length > 0 && sansTraitement > 0 && (
        <div className="orch-synthese">
          <Tag data-testid="envs-sans-traitement">
            {sansTraitement} {sansTraitement > 1 ? "environnements sans traitement" : "environnement sans traitement"}
          </Tag>
        </div>
      )}
      {runs.length > 0 && (
        <>
          {data.truncated && (
            <Alert type="warning" showIcon message={`Liste tronquée : seuls les ${max} jobs les plus récents sont affichés${
                data.first_start && ms(data.first_start) ? `, depuis le ${libelleInstant(ms(data.first_start))}` : ""
              }`} />
          )}
          <div className="orch-pic" data-testid="pic-simultane">
            {pic.count > 0
              ? `Pic : ${pic.count} ${pic.count > 1 ? "jobs simultanés" : "job simultané"}${pic.at ? ` le ${libelleInstant(pic.at)}` : ""}`
              : "Aucun chevauchement"}
            {nbStale(runs) > 0 &&
              ` · + ${libelleStaleNote(nbStale(runs))}, non ${nbStale(runs) > 1 ? "comptés" : "compté"}`}
          </div>
          <div className="carto-frise">
            <svg ref={axeRef} className="orch-axe" aria-hidden="true" />
            <svg ref={svgRef} role="img" aria-label="Frise d'orchestration du serveur" />
          </div>
          <Table
            size="small"
            rowKey="order"
            columns={colonnes}
            dataSource={runs}
            pagination={proprietesPagination({ page, total: runs.length, unite: "jobs", onChange: setPage })}
            onRow={(r) => ({
              onMouseEnter: () => setSurvol(r.order),
              onMouseLeave: () => setSurvol(null),
              onClick: () => ouvrirLogs(r),
              className: survol === null ? "" : lies.has(r.order) ? "orch-lie" : "orch-estompe",
              style: { cursor: "pointer" },
            })}
          />
        </>
      )}
    </div>
  );
}
